/**
 * Landed cost (TASK-0257, DROP-2a): what it costs to get goods from a vendor onto our shelf, shown BESIDE cost
 * ("Cost with freight" on screen), never written into it. Pure and dependency-free, so the API (the read model
 * behind the PO page, Trips, the product page and Sources) and the console (labels, the rule form, the sample
 * line) compute the same numbers and words.
 *
 * How a charge reaches an item:
 *   rule (est.) | actual (a confirmed run cost; DROP-2b: a bill line)
 *     -> target: one order | one trip | location + period
 *     -> the target's PO lines (one level, straight to lines: a trip's charge over every line of every order on it)
 *     -> split by the location's basis (cases | value | weight), largest remainder, cents add back exactly
 *     -> per unit = line freight / qty; cost with freight = unit cost + per unit.
 *
 * Rules (owner, 2026-10-19/20): nothing hard-coded (rates are data); a rule changes the future, not the past
 * (the rule in force on the order's day); actuals beat estimates per order and category; every cent is
 * accounted for (a charge that reaches no line is "not on a line", never dropped).
 *
 * Days are store calendar days `YYYY-MM-DD` (read as UTC, as drop-off.ts). Weeks are Sunday to Saturday.
 */

import { addStoreDays, isStoreDay, storeWeekdayOf } from "./drop-off.js";

/* ── vocabularies ──────────────────────────────────────────────────────── */

export const CHARGE_CATEGORIES = ["drop_off", "pallet", "transfer", "freight", "other"] as const;
export type ChargeCategory = (typeof CHARGE_CATEGORIES)[number];

export const RULE_BASES = [
  "each_drop",
  "each_case",
  "each_pallet",
  "each_trip",
  "weekly",
  "monthly",
  "percent_of_goods",
] as const;
export type RuleBasis = (typeof RULE_BASES)[number];

/** Bases shared over every order reaching the location (the location's own; never a vendor exception). */
export const SHARED_BASES: readonly RuleBasis[] = ["each_trip", "weekly", "monthly"];
export function isSharedBasis(basis: RuleBasis): boolean {
  return SHARED_BASES.includes(basis);
}

export const SPLIT_BASES = ["cases", "value", "weight"] as const;
export type SplitBasis = (typeof SPLIT_BASES)[number];

export const CARRIED_BY = ["own_truck", "carrier", "location"] as const;
export type CarriedBy = (typeof CARRIED_BY)[number];

/** Screen words (spec words "landed", "allocation" never appear alone on screen). */
export const CATEGORY_LABEL: Record<ChargeCategory, string> = {
  drop_off: "Drop-off",
  pallet: "Pallets",
  transfer: "Backhaul",
  freight: "Freight",
  other: "Other",
};

export const BASIS_LABEL: Record<RuleBasis, string> = {
  each_drop: "each drop",
  each_case: "each case",
  each_pallet: "each pallet",
  each_trip: "each trip",
  weekly: "a week",
  monthly: "a month",
  percent_of_goods: "of goods",
};

export const SPLIT_LABEL: Record<SplitBasis, string> = {
  cases: "Cases",
  value: "Value",
  weight: "Weight",
};

/** One line explaining each split (the location dialog). */
export const SPLIT_HELP: Record<SplitBasis, string> = {
  cases: "Each case takes the same share (a case of bagels takes a dock spot like a case of juice).",
  value: "Dearer goods take a bigger share, in proportion to what they cost.",
  weight: "Heavier goods take a bigger share; lines with no weight fall back to cases.",
};

export const CARRIED_BY_LABEL: Record<CarriedBy, string> = {
  own_truck: "Our truck",
  carrier: "A carrier",
  location: "The location",
};

/* ── splitCents: every cent adds back ──────────────────────────────────── */

/**
 * Split `totalCents` over `weights` (non-negative integers, any scale) exactly: floor each share, then give the
 * leftover cents one each to the largest remainders, ties to the lower id (`ids`, default the index). A
 * negative total splits its absolute value and negates. All-zero weights split equally; no weights at all
 * returns [] (the caller keeps the charge as "not on a line").
 */
export function splitCents(
  totalCents: number,
  weights: readonly (number | bigint)[],
  ids?: readonly number[],
): number[] {
  if (!Number.isSafeInteger(totalCents)) throw new RangeError("splitCents: total must be whole cents");
  const n = weights.length;
  if (n === 0) return [];
  const w = weights.map((x) => {
    const b = typeof x === "bigint" ? x : BigInt(x);
    if (typeof x === "number" && !Number.isSafeInteger(x)) throw new RangeError("splitCents: weights are integers");
    if (b < 0n) throw new RangeError("splitCents: weights are never negative");
    return b;
  });
  let sum = w.reduce((a, b) => a + b, 0n);
  const weightsUsed = sum === 0n ? w.map(() => 1n) : w;
  if (sum === 0n) sum = BigInt(n);
  const negative = totalCents < 0;
  const total = BigInt(Math.abs(totalCents));
  const shares: bigint[] = [];
  const rems: bigint[] = [];
  let given = 0n;
  for (const wi of weightsUsed) {
    const exact = total * wi;
    const floor = exact / sum;
    shares.push(floor);
    rems.push(exact % sum);
    given += floor;
  }
  let leftover = Number(total - given);
  if (leftover > 0) {
    const order = [...Array(n).keys()].sort((a, b) => {
      const ra = rems[a]!;
      const rb = rems[b]!;
      if (ra !== rb) return ra > rb ? -1 : 1;
      const ia = ids?.[a] ?? a;
      const ib = ids?.[b] ?? b;
      return ia - ib;
    });
    for (const i of order) {
      if (leftover === 0) break;
      shares[i] = shares[i]! + 1n;
      leftover -= 1;
    }
  }
  return shares.map((s) => (negative ? -Number(s) : Number(s)) || 0);
}

/* ── line weights ──────────────────────────────────────────────────────── */

/** One PO line, as the split reads it. */
export interface LandedLine {
  id: number;
  poId: number;
  /** Lines without a product never take a share. */
  productId: number | null;
  unit: "ea" | "lb";
  orderedQty: number;
  receivedQty: number;
  /** The line's order pack, else the vendor link's pack, else null (1). */
  packSize: number | null;
  /** A weighed line's case count when known. */
  shippedCaseCount: number | null;
  /** unit_cost ?? ordered_unit_cost (cents per ea / lb). */
  unitCostCents: number | null;
  /** True when unitCostCents is the order estimate (no invoice / receipt cost yet). */
  costIsEstimate: boolean;
  /** An `ea` line's weight per unit in lb (product size in oz/lb/g/kg); `lb` lines read 1. */
  lbPerUnit: number | null;
}

export type BasisReason = "weight_unknown";

/** The quantity a line's share is read from: received once the order has any receipt, else ordered. */
export function qtyBasisOf(line: LandedLine, received: boolean): number {
  return received ? line.receivedQty : line.orderedQty;
}

/** A line's weight for one basis as an integer (cases in thousandths, value in cents, weight in milli-lb). */
export function lineWeight(line: LandedLine, basis: SplitBasis, received: boolean): bigint | null {
  if (line.productId === null) return 0n;
  const qty = qtyBasisOf(line, received);
  if (!(qty > 0)) return 0n;
  if (basis === "cases") {
    if (line.unit === "lb")
      return BigInt((line.shippedCaseCount && line.shippedCaseCount > 0 ? line.shippedCaseCount : 1) * 1000);
    const pack = line.packSize && line.packSize > 0 ? line.packSize : 1;
    return BigInt(Math.round((qty * 1000) / pack));
  }
  if (basis === "value") return BigInt(Math.max(0, Math.round(qty * (line.unitCostCents ?? 0))));
  const perUnit = line.unit === "lb" ? 1 : line.lbPerUnit;
  if (perUnit === null || !(perUnit > 0)) return null;
  return BigInt(Math.round(qty * perUnit * 1000));
}

/**
 * The weights of a charge's target lines for a basis. Weight falls back to cases (and says so) when any line
 * that takes a share has no weight. When every line weighs zero on the chosen basis (say, all by value and every
 * line that came in has no cost yet) the split falls back to cases, then to an equal split among the lines with
 * a quantity; a line that took nothing never takes a share while another line did take something. Only when no
 * line has a quantity at all do the weights stay all zero (splitCents then splits equally).
 */
export function lineBasis(
  lines: readonly { line: LandedLine; received: boolean }[],
  basis: SplitBasis,
): { weights: bigint[]; basisUsed: SplitBasis; reason: BasisReason | null } {
  const cases = () => lines.map(({ line, received }) => lineWeight(line, "cases", received) ?? 0n);
  let chosen: { weights: bigint[]; basisUsed: SplitBasis; reason: BasisReason | null };
  if (basis === "weight") {
    const w = lines.map(({ line, received }) => lineWeight(line, "weight", received));
    chosen = w.every((x) => x !== null)
      ? { weights: w as bigint[], basisUsed: "weight", reason: null }
      : { weights: cases(), basisUsed: "cases", reason: "weight_unknown" };
  } else {
    chosen = {
      weights: lines.map(({ line, received }) => lineWeight(line, basis, received) ?? 0n),
      basisUsed: basis,
      reason: null,
    };
  }
  if (lines.length === 0 || chosen.weights.some((x) => x > 0n)) return chosen;
  const byCases = chosen.basisUsed === "cases" ? chosen.weights : cases();
  const weights = byCases.some((x) => x > 0n)
    ? byCases
    : lines.map(({ line, received }) => (line.productId !== null && qtyBasisOf(line, received) > 0 ? 1n : 0n));
  return { weights, basisUsed: "cases", reason: chosen.reason };
}

/** Cases on an order in thousandths (each_case / each_pallet estimates, the trip card). */
export function orderCasesMilli(lines: readonly LandedLine[], received: boolean): number {
  return lines.reduce((sum, line) => sum + Number(lineWeight(line, "cases", received) ?? 0n), 0);
}

/** Goods value of an order in cents (percent_of_goods, the trip card). */
export function orderGoodsCents(lines: readonly LandedLine[], received: boolean): number {
  return lines.reduce((sum, line) => sum + Number(lineWeight(line, "value", received) ?? 0n), 0);
}

/* ── rules ─────────────────────────────────────────────────────────────── */

export interface ChargeRule {
  id: number;
  locationId: number | null;
  vendorId: number | null;
  category: ChargeCategory;
  basis: RuleBasis;
  /** Cents; basis points for percent_of_goods. */
  amountCents: number;
  casesPerPallet: number | null;
  payeeVendorId: number | null;
  startsOn: string;
  endsOn: string | null;
  active: boolean;
}

/**
 * The rule in force on `day`: with a location, the vendor's exception first, then the location's own rule;
 * without one, the vendor's own rule. Inactive rules and days outside starts_on..ends_on never apply.
 */
export function ruleInForce(
  rules: readonly ChargeRule[],
  q: { locationId: number | null; vendorId: number | null; category: ChargeCategory; basis: RuleBasis; day: string },
): ChargeRule | null {
  const live = rules.filter(
    (r) =>
      r.active &&
      r.category === q.category &&
      r.basis === q.basis &&
      r.startsOn <= q.day &&
      (r.endsOn === null || q.day <= r.endsOn),
  );
  if (q.locationId !== null) {
    if (q.vendorId !== null) {
      const exception = live.find((r) => r.locationId === q.locationId && r.vendorId === q.vendorId);
      if (exception) return exception;
    }
    return live.find((r) => r.locationId === q.locationId && r.vendorId === null) ?? null;
  }
  if (q.vendorId === null) return null;
  return live.find((r) => r.locationId === null && r.vendorId === q.vendorId) ?? null;
}

/** `$45.00 each drop`, `3% of goods`, `$4.00 each pallet (40 cases a pallet)`, `$250.00 each trip`. */
export function ruleAmountText(rule: Pick<ChargeRule, "basis" | "amountCents" | "casesPerPallet">): string {
  if (rule.basis === "percent_of_goods") return `${percentText(rule.amountCents)} of goods`;
  const base = `${dollars(rule.amountCents)} ${BASIS_LABEL[rule.basis]}`;
  if (rule.basis === "each_pallet" && rule.casesPerPallet) {
    return `${base} (${trimNumber(rule.casesPerPallet)} cases a pallet)`;
  }
  return base;
}

/** Basis points as `3%`, `2.5%`. */
export function percentText(bp: number): string {
  return `${trimNumber(bp / 100)}%`;
}

function trimNumber(n: number): string {
  return String(Math.round(n * 100) / 100);
}

function dollars(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}$${Math.floor(abs / 100).toLocaleString("en-US")}.${String(abs % 100).padStart(2, "0")}`;
}

/* ── rule validation (the API's body and the console's form) ───────────── */

export interface ChargeRuleInput {
  locationId: number | null;
  vendorId: number | null;
  category: ChargeCategory;
  basis: RuleBasis;
  amountCents: number;
  casesPerPallet: number | null;
  payeeVendorId: number | null;
  startsOn: string;
  endsOn: string | null;
  note: string | null;
  active: boolean;
}

const RULE_KEYS: ReadonlyArray<keyof ChargeRuleInput> = [
  "locationId",
  "vendorId",
  "category",
  "basis",
  "amountCents",
  "casesPerPallet",
  "payeeVendorId",
  "startsOn",
  "endsOn",
  "note",
  "active",
];

export const RULE_NOTE_MAX = 300;
export const RULE_AMOUNT_MAX_CENTS = 10_000_000;

export type ChargeRuleParse = { success: true; data: ChargeRuleInput } | { success: false; errors: string[] };

function idOrNull(v: unknown): number | null | undefined {
  if (v === null) return null;
  if (typeof v === "number" && Number.isInteger(v) && v > 0 && v <= 2_147_483_647) return v;
  return undefined;
}

/**
 * Validate a whole rule (POST body, or the PUT's merge of the stored rule and the change). Every problem is a
 * sentence the operator can act on. `allowKeys` are the caller's own keys (`updatedAt`).
 */
export function parseChargeRule(input: unknown, opts: { allowKeys?: readonly string[] } = {}): ChargeRuleParse {
  if (input === null || typeof input !== "object" || Array.isArray(input)) {
    return { success: false, errors: ["The rule is missing."] };
  }
  const raw = input as Record<string, unknown>;
  const errors: string[] = [];
  const allowed = new Set<string>([...RULE_KEYS, ...(opts.allowKeys ?? [])]);
  for (const key of Object.keys(raw)) if (!allowed.has(key)) errors.push(`"${key}" is not a rule field.`);

  const locationId = idOrNull(raw.locationId ?? null);
  const vendorId = idOrNull(raw.vendorId ?? null);
  const payeeVendorId = idOrNull(raw.payeeVendorId ?? null);
  if (locationId === undefined) errors.push("Location: choose a drop-off location.");
  if (vendorId === undefined) errors.push("Vendor: choose a vendor.");
  if (payeeVendorId === undefined) errors.push("Billed by: choose a vendor, or nobody.");
  if (locationId === null && vendorId === null)
    errors.push("A rule belongs to a drop-off location, a vendor, or both.");

  const category = raw.category;
  if (typeof category !== "string" || !(CHARGE_CATEGORIES as readonly string[]).includes(category)) {
    errors.push("What: drop-off, pallets, backhaul, freight or other.");
  }
  const basis = raw.basis;
  const basisOk = typeof basis === "string" && (RULE_BASES as readonly string[]).includes(basis);
  if (!basisOk)
    errors.push("How: each drop, each case, each pallet, each trip, a week, a month or a percent of goods.");
  if (basisOk && isSharedBasis(basis as RuleBasis)) {
    if (locationId === null) errors.push("Each trip, weekly and monthly charges belong to a drop-off location.");
    else if (vendorId !== null && vendorId !== undefined) {
      errors.push(
        "Each trip, weekly and monthly charges are shared by every vendor at the location; a vendor exception is per drop, per case, per pallet or a percent.",
      );
    }
  }

  const amount = raw.amountCents;
  if (typeof amount !== "number" || !Number.isInteger(amount) || amount < 0) {
    errors.push(basis === "percent_of_goods" ? "Amount: a percent from 0 to 100." : "Amount: zero or more, in cents.");
  } else if (basis === "percent_of_goods" && amount > 10_000) {
    errors.push("Amount: a percent from 0 to 100.");
  } else if (amount > RULE_AMOUNT_MAX_CENTS) {
    errors.push("Amount: at most $100,000.00.");
  }

  let casesPerPallet: number | null = null;
  const cpp = raw.casesPerPallet;
  if (cpp !== undefined && cpp !== null) {
    if (
      typeof cpp !== "number" ||
      !Number.isFinite(cpp) ||
      cpp <= 0 ||
      cpp > 999_999 ||
      Math.round(cpp * 100) !== cpp * 100
    ) {
      errors.push("Cases a pallet: a number above 0 (up to two decimals).");
    } else casesPerPallet = cpp;
  }
  if (basis === "each_pallet" && casesPerPallet === null && !errors.some((e) => e.startsWith("Cases a pallet"))) {
    errors.push("Cases a pallet: say how many cases make a pallet, so the pallets can be estimated.");
  }
  if (basis !== "each_pallet") casesPerPallet = null;

  const startsOn = raw.startsOn;
  if (!isStoreDay(startsOn)) errors.push("From: a day like 2026-02-17.");
  const endsOn = raw.endsOn ?? null;
  if (endsOn !== null && !isStoreDay(endsOn)) errors.push("To: a day like 2026-02-16, or blank for no end.");
  if (isStoreDay(startsOn) && isStoreDay(endsOn) && endsOn < startsOn) errors.push("To: on or after From.");

  let note: string | null = null;
  if (raw.note !== undefined && raw.note !== null) {
    if (typeof raw.note !== "string") errors.push("Note: text.");
    else {
      const t = raw.note.trim();
      if (/[\r\n]/.test(t)) errors.push("Note: one line.");
      if (t.length > RULE_NOTE_MAX) errors.push(`Note: at most ${RULE_NOTE_MAX} characters.`);
      note = t === "" ? null : t;
    }
  }
  const active = raw.active === undefined ? true : raw.active;
  if (typeof active !== "boolean") errors.push("Active: on or off.");

  if (errors.length > 0) return { success: false, errors: [...new Set(errors)] };
  return {
    success: true,
    data: {
      locationId: locationId as number | null,
      vendorId: vendorId as number | null,
      category: category as ChargeCategory,
      basis: basis as RuleBasis,
      amountCents: amount as number,
      casesPerPallet,
      payeeVendorId: payeeVendorId as number | null,
      startsOn: startsOn as string,
      endsOn: endsOn as string | null,
      note,
      active: active as boolean,
    },
  };
}

/** True when two rules' date ranges share a day. */
export function rangesOverlap(
  a: { startsOn: string; endsOn: string | null },
  b: { startsOn: string; endsOn: string | null },
): boolean {
  return (a.endsOn === null || b.startsOn <= a.endsOn) && (b.endsOn === null || a.startsOn <= b.endsOn);
}

/* ── periods ───────────────────────────────────────────────────────────── */

/** The Sunday that starts the store week of `day`. */
export function weekStartOf(day: string): string {
  return addStoreDays(day, -storeWeekdayOf(day));
}

/** The first of the month of `day`. */
export function monthStartOf(day: string): string {
  return `${day.slice(0, 7)}-01`;
}

/* ── the read model ────────────────────────────────────────────────────── */

export interface LandedOrder {
  id: number;
  vendorId: number | null;
  locationId: number | null;
  /** The day the rules are read on: the drop day, else the store day, else the day it was sent. */
  ruleDay: string | null;
  /** The day expected at the store (weekly / monthly periods, suggested trips). */
  storeDay: string | null;
  /** Any receipt: shares follow received quantities. */
  received: boolean;
  /** The trip it is on (`trip:<id>` confirmed, `sug:<location>:<day>` suggested), or null. */
  tripKey: string | null;
  lines: LandedLine[];
  /**
   * A plan (a draft asked about alone, DROP-2b review F6): it is estimated as joining its trip and periods, but a
   * placed bill was never billed for it, so no placed charge on a trip or period is shared onto it.
   */
  plan?: boolean;
}

export interface LandedLocation {
  id: number;
  chargeSplit: SplitBasis;
}

export interface LandedTrip {
  key: string;
  locationId: number;
  arrivesOn: string;
  orderIds: number[];
}

/** A charge that is not an estimate: our own truck's confirmed run cost (DROP-2a); a placed bill line (DROP-2b). */
export interface ActualCharge {
  key: string;
  category: ChargeCategory;
  amountCents: number;
  source: "run_cost" | "charge";
  target:
    | { kind: "order"; poId: number }
    | { kind: "trip"; tripKey: string }
    | { kind: "location_period"; locationId: number; from: string; to: string };
}

/**
 * A covered period (DROP-2b): a placed bill said "this location's <category> for these drop days". An order of
 * the location whose day falls in it and that no charge of the category reached reads billed $0.00 ("not
 * charged"), never its estimate.
 */
export interface CoveredPeriod {
  key: string;
  locationId: number;
  category: ChargeCategory;
  from: string;
  to: string;
}

export interface LandedFacts {
  orders: LandedOrder[];
  locations: LandedLocation[];
  trips: LandedTrip[];
  rules: ChargeRule[];
  actuals: ActualCharge[];
  /** DROP-2b: periods a placed bill covered (billed $0.00 for the orders nothing reached). */
  covered?: CoveredPeriod[];
}

export type ChargeStatus = "est" | "actual";
export type TargetKind = "order" | "trip" | "week" | "month" | "location_period";

/** One charge (an applied rule or an actual), whole. */
export interface LandedCharge {
  key: string;
  category: ChargeCategory;
  status: ChargeStatus;
  source: "rule" | "run_cost" | "charge";
  ruleId: number | null;
  basis: RuleBasis | null;
  targetKind: TargetKind;
  /** The trip key for a trip charge; `week:<loc>:<day>` / `month:<loc>:<day>` for a period. */
  targetKey: string;
  /** The orders the charge was split over. */
  orderIds: number[];
  totalCents: number;
  basisUsed: SplitBasis;
  reason: BasisReason | null;
  /** Cents that reached no line (no product lines in the target). */
  notOnLineCents: number;
}

export interface LinePart {
  chargeKey: string;
  category: ChargeCategory;
  status: ChargeStatus;
  cents: number;
}

export interface LineLanded {
  lineId: number;
  poId: number;
  qtyBasis: number;
  /** Sum of the parts that count (an estimate replaced by an actual of its category is dropped). */
  freightCents: number;
  /** Any counted part is an estimate. */
  estimate: boolean;
  parts: LinePart[];
}

export interface OrderChargeShare {
  chargeKey: string;
  category: ChargeCategory;
  status: ChargeStatus;
  /** This order's share. */
  cents: number;
  /** False when an actual of the category replaced this estimate for the order. */
  counted: boolean;
}

export interface OrderLanded {
  poId: number;
  shares: OrderChargeShare[];
  totalCents: number;
  estCents: number;
  actualCents: number;
  /** Order-target charges that reached no line of this order. */
  notOnLineCents: number;
}

export interface LandedResult {
  charges: Map<string, LandedCharge>;
  lines: Map<number, LineLanded>;
  orders: Map<number, OrderLanded>;
}

const PER_ORDER_BASES: readonly RuleBasis[] = ["each_drop", "each_case", "each_pallet", "percent_of_goods"];

/** The estimate of one per-order rule for one order, in cents. */
export function perOrderAmount(rule: ChargeRule, order: Pick<LandedOrder, "lines" | "received">): number {
  switch (rule.basis) {
    case "each_drop":
      return rule.amountCents;
    case "each_case": {
      const casesMilli = orderCasesMilli(order.lines, order.received);
      return Math.round((rule.amountCents * casesMilli) / 1000);
    }
    case "each_pallet": {
      const casesMilli = orderCasesMilli(order.lines, order.received);
      const cppHundredths = Math.round((rule.casesPerPallet ?? 1) * 100);
      const pallets = casesMilli <= 0 ? 0 : Math.ceil((casesMilli * 100) / (cppHundredths * 1000));
      return rule.amountCents * pallets;
    }
    case "percent_of_goods":
      return Math.round((rule.amountCents * orderGoodsCents(order.lines, order.received)) / 10_000);
    default:
      return 0;
  }
}

/**
 * Every charge (rules in force, actuals), split onto the lines of its target. Pure: the API loads the facts
 * (the orders in scope AND every order sharing a trip or period with them), this decides the numbers.
 */
export function computeLanded(facts: LandedFacts): LandedResult {
  const orders = new Map(facts.orders.map((o) => [o.id, o]));
  const splitOf = new Map(facts.locations.map((l) => [l.id, l.chargeSplit]));
  const charges = new Map<string, LandedCharge>();
  const lineParts = new Map<number, LinePart[]>();
  const orderShares = new Map<number, OrderChargeShare[]>();
  // An order charge that reached no product line, kept by category and status: an estimate an actual replaced is
  // not counted (review R3-3), as for the shares.
  const orderNotOnLine = new Map<number, { category: ChargeCategory; status: ChargeStatus; cents: number }[]>();

  const place = (
    base: Omit<LandedCharge, "basisUsed" | "reason" | "notOnLineCents" | "orderIds">,
    orderIds: number[],
    split: SplitBasis,
  ) => {
    const members = orderIds.map((id) => orders.get(id)).filter((o): o is LandedOrder => o !== undefined);
    const targets = members.flatMap((o) =>
      o.lines.filter((l) => l.productId !== null).map((line) => ({ line, received: o.received })),
    );
    const basis = lineBasis(targets, split);
    const shares = splitCents(
      base.totalCents,
      basis.weights,
      targets.map((t) => t.line.id),
    );
    const charge: LandedCharge = {
      ...base,
      orderIds: members.map((o) => o.id),
      basisUsed: basis.basisUsed,
      reason: basis.reason,
      notOnLineCents: targets.length === 0 ? base.totalCents : 0,
    };
    charges.set(charge.key, charge);
    const perOrder = new Map<number, number>();
    targets.forEach((t, i) => {
      const cents = shares[i] ?? 0;
      const list = lineParts.get(t.line.id) ?? [];
      list.push({ chargeKey: charge.key, category: charge.category, status: charge.status, cents });
      lineParts.set(t.line.id, list);
      perOrder.set(t.line.poId, (perOrder.get(t.line.poId) ?? 0) + cents);
    });
    for (const o of members) {
      const list = orderShares.get(o.id) ?? [];
      list.push({
        chargeKey: charge.key,
        category: charge.category,
        status: charge.status,
        cents: perOrder.get(o.id) ?? 0,
        counted: true,
      });
      orderShares.set(o.id, list);
    }
    if (targets.length === 0 && base.targetKind === "order" && members[0]) {
      const list = orderNotOnLine.get(members[0].id) ?? [];
      list.push({ category: base.category, status: base.status, cents: base.totalCents });
      orderNotOnLine.set(members[0].id, list);
    }
  };

  // `${poId}:${category}` estimated by the vendor's own rule (it bills its own freight: a location's bill does not
  // speak for it, step 5).
  const vendorOwnEst = new Set<string>();

  // 1. Per-order rules: the location's (exception first) and the vendor's own.
  for (const o of facts.orders) {
    if (o.ruleDay === null) continue;
    const day = o.ruleDay;
    const seen = new Set<string>();
    const candidates = facts.rules.filter(
      (r) =>
        PER_ORDER_BASES.includes(r.basis) &&
        ((o.locationId !== null &&
          r.locationId === o.locationId &&
          (r.vendorId === null || r.vendorId === o.vendorId)) ||
          (o.vendorId !== null && r.locationId === null && r.vendorId === o.vendorId)),
    );
    for (const r of candidates) {
      const scope = r.locationId === null ? "vendor" : "location";
      const sig = `${scope}:${r.category}:${r.basis}`;
      if (seen.has(sig)) continue;
      seen.add(sig);
      const rule = ruleInForce(facts.rules, {
        locationId: scope === "location" ? o.locationId : null,
        vendorId: o.vendorId,
        category: r.category,
        basis: r.basis,
        day,
      });
      if (!rule) continue;
      if (scope === "vendor") vendorOwnEst.add(`${o.id}:${rule.category}`);
      const split: SplitBasis =
        scope === "location" && o.locationId !== null ? (splitOf.get(o.locationId) ?? "cases") : "cases";
      place(
        {
          key: `rule:${rule.id}:po:${o.id}`,
          category: rule.category,
          status: "est",
          source: "rule",
          ruleId: rule.id,
          basis: rule.basis,
          targetKind: "order",
          targetKey: `po:${o.id}`,
          totalCents: perOrderAmount(rule, o),
        },
        [o.id],
        split,
      );
    }
  }

  // 2. Each trip: every each_trip rule of the location in force on the day it reaches the store.
  for (const trip of facts.trips) {
    const tripRules = facts.rules.filter((r) => r.basis === "each_trip" && r.locationId === trip.locationId);
    const cats = [...new Set(tripRules.map((r) => r.category))];
    for (const category of cats) {
      const rule = ruleInForce(facts.rules, {
        locationId: trip.locationId,
        vendorId: null,
        category,
        basis: "each_trip",
        day: trip.arrivesOn,
      });
      if (!rule) continue;
      place(
        {
          key: `rule:${rule.id}:${trip.key}`,
          category,
          status: "est",
          source: "rule",
          ruleId: rule.id,
          basis: "each_trip",
          targetKind: "trip",
          targetKey: trip.key,
          totalCents: rule.amountCents,
        },
        trip.orderIds,
        splitOf.get(trip.locationId) ?? "cases",
      );
    }
  }

  // 3. Weekly / monthly: shared over the location's orders reaching the store in that week / month; the rule
  //    in force on the period's first day.
  for (const basis of ["weekly", "monthly"] as const) {
    const groups = new Map<string, { locationId: number; start: string; orderIds: number[] }>();
    for (const o of facts.orders) {
      if (o.locationId === null || o.storeDay === null) continue;
      const start = basis === "weekly" ? weekStartOf(o.storeDay) : monthStartOf(o.storeDay);
      const key = `${basis === "weekly" ? "week" : "month"}:${o.locationId}:${start}`;
      const g = groups.get(key) ?? { locationId: o.locationId, start, orderIds: [] };
      g.orderIds.push(o.id);
      groups.set(key, g);
    }
    for (const [key, g] of groups) {
      const cats = [
        ...new Set(
          facts.rules.filter((r) => r.basis === basis && r.locationId === g.locationId).map((r) => r.category),
        ),
      ];
      for (const category of cats) {
        const rule = ruleInForce(facts.rules, {
          locationId: g.locationId,
          vendorId: null,
          category,
          basis,
          day: g.start,
        });
        if (!rule) continue;
        place(
          {
            key: `rule:${rule.id}:${key}`,
            category,
            status: "est",
            source: "rule",
            ruleId: rule.id,
            basis,
            targetKind: basis === "weekly" ? "week" : "month",
            targetKey: key,
            totalCents: rule.amountCents,
          },
          g.orderIds,
          splitOf.get(g.locationId) ?? "cases",
        );
      }
    }
  }

  // 4. Actuals.
  const tripsByKey = new Map(facts.trips.map((t) => [t.key, t]));
  const actualised = new Set<string>(); // `${poId}:${category}`
  for (const a of facts.actuals) {
    let orderIds: number[];
    let split: SplitBasis;
    let targetKind: TargetKind;
    let targetKey: string;
    if (a.target.kind === "order") {
      orderIds = [a.target.poId];
      targetKind = "order";
      const o = orders.get(a.target.poId);
      // A vendor's own freight splits by cases, billed as estimated (brief: "cases for a vendor's own freight";
      // review R3-4): only the location's charges use its basis.
      const vendorOwn = vendorOwnEst.has(`${a.target.poId}:${a.category}`);
      split = o?.locationId != null && !vendorOwn ? (splitOf.get(o.locationId) ?? "cases") : "cases";
      targetKey = `po:${a.target.poId}`;
    } else if (a.target.kind === "trip") {
      const trip = tripsByKey.get(a.target.tripKey);
      orderIds = trip?.orderIds ?? [];
      split = trip ? (splitOf.get(trip.locationId) ?? "cases") : "cases";
      targetKind = "trip";
      targetKey = a.target.tripKey;
    } else {
      const t = a.target;
      orderIds = facts.orders
        .filter((o) => {
          const d = o.ruleDay ?? o.storeDay;
          return o.locationId === t.locationId && d !== null && t.from <= d && d <= t.to;
        })
        .map((o) => o.id);
      split = splitOf.get(t.locationId) ?? "cases";
      targetKind = "location_period";
      targetKey = `period:${t.locationId}:${t.from}:${t.to}`;
    }
    // A placed bill is never shared onto a plan: the plan keeps its estimate (review F6).
    if (a.source === "charge") orderIds = orderIds.filter((id) => orders.get(id)?.plan !== true);
    // A placed freight / other bill on a trip or a location period does not speak for a vendor that bills its own
    // (a vendor-only rule estimates it): it keeps its estimate until its own bill is placed (review R2-2, as the
    // covers in step 5). A bill placed on that order itself still replaces the estimate.
    if (a.source === "charge" && a.target.kind !== "order" && (a.category === "freight" || a.category === "other")) {
      const category = a.category;
      orderIds = orderIds.filter((id) => !vendorOwnEst.has(`${id}:${category}`));
    }
    for (const id of orderIds) actualised.add(`${id}:${a.category}`);
    place(
      {
        key: a.key,
        category: a.category,
        status: "actual",
        source: a.source,
        ruleId: null,
        basis: null,
        targetKind,
        targetKey,
        totalCents: a.amountCents,
      },
      orderIds,
      split,
    );
  }

  // 5. Covered periods (DROP-2b): an order of the location in the period that no actual of the category reached
  //    reads billed $0.00 (a zero actual on the order), which replaces its estimate. A vendor that bills its own
  //    freight / other charges (a vendor-only rule estimates it) is not spoken for by the location's bill: it keeps
  //    its estimate until its own bill is placed (review R1b).
  for (const c of facts.covered ?? []) {
    const ownBilled = c.category === "freight" || c.category === "other";
    for (const o of facts.orders) {
      const d = o.ruleDay ?? o.storeDay;
      if (o.locationId !== c.locationId || d === null || d < c.from || d > c.to) continue;
      if (o.plan === true) continue;
      const k = `${o.id}:${c.category}`;
      if (actualised.has(k)) continue;
      if (ownBilled && vendorOwnEst.has(k)) continue;
      actualised.add(k);
      place(
        {
          key: `${c.key}:po:${o.id}`,
          category: c.category,
          status: "actual",
          source: "charge",
          ruleId: null,
          basis: null,
          targetKind: "order",
          targetKey: `po:${o.id}`,
          totalCents: 0,
        },
        [o.id],
        splitOf.get(c.locationId) ?? "cases",
      );
    }
  }

  // Actuals beat estimates, per order and category.
  const lines = new Map<number, LineLanded>();
  for (const o of facts.orders) {
    for (const line of o.lines) {
      const parts = lineParts.get(line.id) ?? [];
      const counted = parts.filter((p) => p.status === "actual" || !actualised.has(`${o.id}:${p.category}`));
      lines.set(line.id, {
        lineId: line.id,
        poId: o.id,
        qtyBasis: qtyBasisOf(line, o.received),
        freightCents: counted.reduce((s, p) => s + p.cents, 0),
        estimate: counted.some((p) => p.status === "est" && p.cents !== 0),
        parts: counted,
      });
    }
  }
  const ordersOut = new Map<number, OrderLanded>();
  for (const o of facts.orders) {
    const shares = (orderShares.get(o.id) ?? []).map((s) => ({
      ...s,
      counted: s.status === "actual" || !actualised.has(`${o.id}:${s.category}`),
    }));
    const counted = shares.filter((s) => s.counted);
    ordersOut.set(o.id, {
      poId: o.id,
      shares,
      totalCents: counted.reduce((s, x) => s + x.cents, 0),
      estCents: counted.filter((x) => x.status === "est").reduce((s, x) => s + x.cents, 0),
      actualCents: counted.filter((x) => x.status === "actual").reduce((s, x) => s + x.cents, 0),
      notOnLineCents: (orderNotOnLine.get(o.id) ?? [])
        .filter((x) => x.status === "actual" || !actualised.has(`${o.id}:${x.category}`))
        .reduce((a, x) => a + x.cents, 0),
    });
  }
  return { charges, lines, orders: ordersOut };
}

/** Per unit freight (exact, cents) and cost with freight (rounded cents); null when the line has no quantity. */
export function perUnitOf(
  freightCents: number,
  qtyBasis: number,
  unitCostCents: number | null,
): { perUnitExact: number | null; perUnitCents: number | null; costWithFreightCents: number | null } {
  if (!(qtyBasis > 0)) return { perUnitExact: null, perUnitCents: null, costWithFreightCents: null };
  const exact = freightCents / qtyBasis;
  return {
    perUnitExact: exact,
    perUnitCents: Math.round(exact),
    costWithFreightCents: unitCostCents === null ? null : Math.round(unitCostCents + exact),
  };
}

/* ── DROP-2b: charge lines on bills ────────────────────────────────────── */

/** What a bill line is, read from its words (the parser's `chargeKind` uses the same list). */
export const CHARGE_KINDS = ["drop_off", "pallet", "transfer", "freight", "deposit", "other"] as const;
export type ChargeKind = (typeof CHARGE_KINDS)[number];

/** The charge category a kind is placed as by default (`deposit` is never placed: it is not freight). */
export function categoryOfKind(kind: ChargeKind): ChargeCategory | null {
  if (kind === "deposit") return null;
  return kind;
}

/**
 * Charge words, in the order they are tried. Words only: never a vendor, location or carrier name (owner rule:
 * nothing hard-coded). Sales tax is NOT freight (owner decision 3): a tax line is never a charge.
 */
const KIND_PATTERNS: ReadonlyArray<readonly [ChargeKind, RegExp]> = [
  ["deposit", /\b(DEPOSITS?|DEP|CRV|BOTTLE\s+DEP\w*|BTL\s+DEP\w*|CRATE\s+DEP\w*)\b/],
  ["drop_off", /\b(DOCKING|DOCK\s+(FEE|CHARGE|CHG)|RECEIVING\s+(FEE|CHARGE|CHG)|DROP\s*OFF\s+(FEE|CHARGE|CHG))\b/],
  ["transfer", /\b(BACK\s*HAULS?|BACKHAULS?|TRANSPORT(ATION)?|PICK\s*UP\s*(&|AND)\s*DELIVERY)\b/],
  ["pallet", /\bPALLETS?\b/],
  [
    "freight",
    // Not a bare "FRT": product names use it ("... WHT FRT BOTTLE"); the inbox shows freight as words.
    /\b(FREIGHT|DELIVERY\s+(CHARGE|CHG|FEE)|DEL\s+(CHARGE|CHG|FEE)|FUEL\s*(SURCHARGE|SUR|CHARGE|CHG|FEE)|SURCHARGE|SHIPPING(\s*(&|AND)\s*HANDLING)?|HANDLING\s+(FEE|CHARGE))\b/,
  ],
];

/**
 * Classify a bill line by its words: `{ kind }` for a charge (docking, pallets, backhaul, freight / delivery /
 * fuel, deposit), or null for anything else (goods, and sales tax). Never decides on a vendor's name.
 */
export function classifyChargeLine(line: {
  itemCode?: string | null | undefined;
  description?: string | null | undefined;
}): { kind: ChargeKind } | null {
  const text = `${line.itemCode ?? ""} ${line.description ?? ""}`.toUpperCase().replace(/\s+/g, " ").trim();
  if (text === "") return null;
  // A charge word wins ("FREIGHT TAXABLE" is freight); a line that only says tax is sales tax, never freight.
  for (const [kind, re] of KIND_PATTERNS) if (re.test(text)) return { kind };
  return null;
}

/** Words that are never a name on a charge line (the charge itself, price notes, filler). */
const NOT_A_NAME = new Set([
  "DOCKING",
  "DOCK",
  "CHARGE",
  "CHARGES",
  "CHG",
  "FEE",
  "FEES",
  "RECEIVING",
  "PALLET",
  "PALLETS",
  "PU",
  "P/U",
  "PICKUP",
  "NO",
  "NEW",
  "PRICE",
  "MISC",
  "MISCELLANEOUS",
  "FOR",
  "AND",
  "OF",
  "THE",
  "X",
  "EA",
  "EACH",
  "DROP",
  "DROPS",
  "OFF",
  "BACKHAUL",
  "TRANSPORT",
  "FREIGHT",
]);

export interface BilledName {
  /** The name as printed, tidied (`KAYCO`, `ICE CREAM`). */
  text: string;
  /** Units billed for it (a leading or trailing count: `2 KAYCO`, `2KAYCO`, `LEIBER 4`). */
  count: number;
}

/**
 * The names on a charge line and how many units each (a docking line: one unit per vendor drop). Strips the
 * charge words, money and "NEW PRICE $45" notes; splits on `/ , . -` and wide gaps; a number between two words
 * belongs to the word after it ("LIEBER 2 KAYCO" = LIEBER + 2 KAYCO); a number at the end belongs to the word
 * before it ("LEIBER 4"). Apostrophes are dropped (LIEBER'S = LIEBERS), never split on.
 */
export function parseBilledNames(description: string | null | undefined): { names: BilledName[]; total: number } {
  let text = (description ?? "").toUpperCase();
  text = text.replace(/NEW\s+PRICE\s*:?\s*\$?\s*\d+(\.\d+)?/g, " ");
  text = text.replace(/\$\s*\d[\d,]*(\.\d+)?/g, " ");
  text = text.replace(/\b\d+\.\d+\b/g, " "); // prices
  text = text.replace(/['’`]/g, "");
  text = text.replace(/\bNO\s+CHARGE\b/g, " ");
  const pieces = text.split(/[/,.\-;:()&+|]+|\s{2,}/);
  const names: BilledName[] = [];
  for (const piece of pieces) {
    const words = piece
      .replace(/(\d+)([A-Z])/g, "$1 $2")
      .replace(/([A-Z])(\d+)/g, "$1 $2")
      .split(/\s+/)
      .filter((w) => w !== "" && !NOT_A_NAME.has(w));
    let pending: number | null = null;
    let current: string[] = [];
    let currentCount: number | null = null;
    const flush = () => {
      if (current.length > 0) names.push({ text: current.join(" "), count: currentCount ?? 1 });
      current = [];
      currentCount = null;
    };
    for (const w of words) {
      if (/^\d+$/.test(w)) {
        const n = Number(w);
        if (!(n > 0 && n < 100)) continue;
        if (current.length > 0) {
          // A number after a name: the next word's count, unless the name ends here.
          flush();
        }
        pending = n;
        continue;
      }
      if (!/[A-Z]/.test(w)) continue;
      if (pending !== null) {
        flush();
        currentCount = pending;
        pending = null;
      }
      current.push(w);
    }
    if (pending !== null) {
      // A trailing count belongs to the name before it.
      const last = names[names.length - 1];
      if (current.length === 0 && last && last.count === 1) last.count = pending;
      else if (current.length > 0) currentCount = pending;
    }
    flush();
  }
  const merged: BilledName[] = [];
  for (const n of names) {
    const same = merged.find((m) => m.text === n.text);
    if (same) same.count += n.count;
    else merged.push({ ...n });
  }
  return { names: merged, total: merged.reduce((s, n) => s + n.count, 0) };
}

/** A name as compared: letters and digits only, upper case. */
export function normalizeBilledName(name: string): string {
  return name.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function editDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (Math.abs(m - n) > 2) return 3;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i += 1) {
    const cur = [i];
    for (let j = 1; j <= n; j += 1) {
      cur[j] = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n]!;
}

export type NameMatchHow = "alias" | "exact" | "prefix" | "close";

export interface NameMatch {
  vendorId: number;
  how: NameMatchHow;
  /** Lower is better: alias 0, exact 1, prefix 2, close 3 + distance. */
  rank: number;
}

/**
 * Rank vendors for a printed name: a learned alias first, then the normalised name equal to a vendor's name or
 * its first word, then a prefix either way (at least 4 letters), then an edit distance of at most 2 (names of 4
 * letters or more). Returns every candidate that matches, best first; never forces a match.
 */
export function matchBilledName(
  name: string,
  candidates: readonly { vendorId: number; name: string }[],
  aliases: readonly { aliasNorm: string; vendorId: number }[] = [],
): NameMatch[] {
  const norm = normalizeBilledName(name);
  if (norm === "") return [];
  const out = new Map<number, NameMatch>();
  const offer = (m: NameMatch) => {
    const was = out.get(m.vendorId);
    if (!was || m.rank < was.rank) out.set(m.vendorId, m);
  };
  for (const a of aliases) if (a.aliasNorm === norm) offer({ vendorId: a.vendorId, how: "alias", rank: 0 });
  for (const c of candidates) {
    const whole = normalizeBilledName(c.name);
    const first = normalizeBilledName(c.name.split(/[\s/,&-]+/)[0] ?? "");
    const forms = [...new Set([whole, first].filter((f) => f.length > 0))];
    if (forms.includes(norm)) {
      offer({ vendorId: c.vendorId, how: "exact", rank: 1 });
      continue;
    }
    if (norm.length >= 4 && forms.some((f) => f.startsWith(norm) || (f.length >= 4 && norm.startsWith(f)))) {
      offer({ vendorId: c.vendorId, how: "prefix", rank: 2 });
      continue;
    }
    if (norm.length >= 4) {
      const d = Math.min(...forms.map((f) => editDistance(norm, f)));
      if (d <= 2) offer({ vendorId: c.vendorId, how: "close", rank: 3 + d });
    }
  }
  return [...out.values()].sort((a, b) => a.rank - b.rank || a.vendorId - b.vendorId);
}

/** The one vendor to preselect for a name, or null when the best is not clearly one vendor (never forced). */
export function confidentMatch(matches: readonly NameMatch[]): number | null {
  const best = matches[0];
  if (!best) return null;
  const tied = matches.filter((m) => m.rank === best.rank);
  if (tied.length > 1) return null;
  return best.vendorId;
}
